/**
 * ConnectionProgress
 * -----------------------------------------------------------------
 * Compact 3-step strip shown at the top of the Home view while the
 * Fleet API is streaming pieces of Tesla data. Turns the awkward
 * ~15-second wait after sign-in into a live progress moment so the
 * user can see exactly which piece of data has arrived and which is
 * still in flight.
 *
 * Steps (in order):
 *  1. Session — /api/tesla/status returned connected
 *  2. Vehicle profile — /api/tesla/vehicles returned at least one car
 *  3. Battery pack — /api/tesla/vehicle_data returned battery + range
 *  4. Location — vehicle_data included GPS coords
 *
 * Auto-hides once every step is ✓ (parent unmounts it when
 * `vehicle.live` flips true).
 */

import React from "react";
import { Check, Loader2 } from "lucide-react";

const Step = ({ label, done }) => (
  <div className="flex items-center gap-1.5 min-w-0" data-testid={`conn-step-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    {done ? (
      <span
        className="w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0"
        style={{ background: "rgba(57,255,20,0.14)", border: "1px solid rgba(57,255,20,0.5)" }}
        aria-label={`${label} ready`}
      >
        <Check className="w-2.5 h-2.5" style={{ color: "#39FF14" }} strokeWidth={3} />
      </span>
    ) : (
      <span
        className="w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0"
        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)" }}
        aria-label={`${label} loading`}
      >
        <Loader2
          className="w-2.5 h-2.5 animate-spin"
          style={{ color: "#71717A" }}
          strokeWidth={2.5}
        />
      </span>
    )}
    <span
      className={`font-mono text-[9px] uppercase tracking-[0.14em] truncate ${
        done ? "text-white" : "text-zinc-500"
      }`}
    >
      {label}
    </span>
  </div>
);

export const ConnectionProgress = ({ live }) => {
  const gotVehicles = !!(live?.vehicles?.length);
  const gotBattery = !!(live?.data?.battery?.level != null);
  const gotLocation = !!(live?.data?.location?.lat != null);

  const completed = [gotVehicles, gotBattery, gotLocation].filter(Boolean).length;
  const total = 3;
  const pct = Math.round((completed / total) * 100);

  return (
    <div
      data-testid="connection-progress"
      className="rounded-xl border border-[#00E5FF]/25 bg-[#00E5FF]/[0.04] px-3 py-2.5"
    >
      <div className="flex items-baseline justify-between mb-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#00E5FF]">
          Waking your Tesla…
        </div>
        <div className="font-mono text-[10px] text-zinc-400" data-testid="conn-pct">
          {pct}%
        </div>
      </div>

      <div className="h-1 rounded-full bg-white/5 overflow-hidden mb-2.5">
        <div
          className="h-full transition-[width] duration-500 ease-out"
          style={{
            width: `${pct}%`,
            background: "linear-gradient(90deg, #00E5FF 0%, #39FF14 100%)",
            boxShadow: "0 0 8px rgba(57,255,20,0.5)",
          }}
        />
      </div>

      <div className="flex items-center justify-between gap-2">
        <Step label="Profile" done={gotVehicles} />
        <span className="text-zinc-700 text-[10px]">·</span>
        <Step label="Battery" done={gotBattery} />
        <span className="text-zinc-700 text-[10px]">·</span>
        <Step label="Location" done={gotLocation} />
      </div>
    </div>
  );
};
